/** Phase F (barcelona package): Barcelona, the one trip abroad. Lines in “quotes” are English practice and stay in English in every locale. */
export const enBarcelona = {
  barcelona: {
    mapNames: { barcelona: 'Barcelona' },
    zones: {
      bc_city: 'Barcelona',
      bc_airport: 'El Prat airport · arrivals',
      bc_guell: 'Park Güell',
      bc_sagrada: 'Sagrada Família & the Eixample',
      bc_rambla: 'La Rambla',
      bc_gotic: 'Barri Gòtic',
      bc_park: 'Parc de la Ciutadella',
      bc_beach: 'Port Vell & the Barceloneta',
    },
    objects: {
      bc_flight: 'Flight home · El Prat',
      bc_aerobus: 'Aerobús stop (to Plaça Catalunya)',
      bc_metroGuell: 'Metro L3 · Lesseps',
      bc_metroSagrada: 'Metro L5 · Sagrada Família',
      bc_metroLiceu: 'Metro L3 · Liceu',
      bc_metroBeach: 'Metro L4 · Barceloneta',
      bc_guellBench: 'Serpentine bench',
      bc_dragon: 'The dragon (el drac)',
      bc_sagrada: 'Sagrada Família',
      bc_canaletes: 'Canaletes fountain',
      bc_flowers: 'Flower kiosk',
      bc_statue: 'Human statue',
      bc_boqueria: 'La Boqueria market',
      bc_columbus: 'Columbus column',
      bc_cathedral: 'Cathedral of Santa Eulàlia',
      bc_xurros: 'Churros & chocolate stand',
      bc_castellers: 'Castellers (human tower)',
      bc_petanca: 'Petanca court',
      bc_chiringuito: 'Beach bar (xiringuito)',
    },
    npc: {
      bc_florist: 'Flower seller',
      bc_guide: 'Sagrada Família guide',
      bc_waiter: 'Beach-bar waiter',
      bc_avi: 'Grandpa with the bread',
      bc_tourist: 'Lost Taiwanese tourist',
      bc_busker: 'Street guitarist',
      bc_sardanista: 'Sardana dancer',
      bc_petancaMan: 'Petanca player',
      bc_juicer: 'Juice seller',
      bc_kid: 'Girl on roller skates',
      bc_staff: 'Airport information',
      bc_casteller: 'Cap de colla',
      bc_painter: 'Portrait painter',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    // --- Getting around -------------------------------------------------------------------------
    metro: {
      text: 'Barcelona metro. Which stop?',
      guell: '🟢 L3 · Lesseps (Park Güell)',
      sagrada: '🔵 L5 · Sagrada Família',
      liceu: '🟢 L3 · Liceu (La Rambla)',
      beach: '🟡 L4 · Barceloneta (beach)',
      airport: '🟠 L9 · Airport T1',
      riding: '🚇 Mind the gap… the doors close with a whistle, and off you go.',
      fact: 'The Barcelona metro opened in 1924 — one of the oldest in Spain. A T-casual card gives you ten rides.',
    },
    aerobus: {
      text: 'The Aerobús A1 runs from T1 to Plaça Catalunya every five minutes — about 35 minutes into the centre.',
      go: '🚌 Ride to Plaça Catalunya',
      riding: 'The blue bus swings onto the motorway… Montjuïc on the left… Plaça Catalunya!',
      arrived: 'Plaça Catalunya: the pigeons, the fountains, and La Rambla starts right there, going down to the sea.',
    },

    // --- Sights ----------------------------------------------------------------------------------
    sagrada: {
      text: 'The Sagrada Família: Antoni Gaudí’s basilica. Building started in 1882 and it is STILL not finished — the cranes are part of the view. When done it will have 18 towers.',
      ask: '🗣️ Ask the guide (English)',
      q: 'How do you ask the guide when the basilica will be finished?',
      a: ['Excuse me, when will the basilica be finished?', 'Finish when basilica?', 'Basilica done now, yes?'],
      right: '“Good question! The tallest tower, for Jesus, should be ready soon — 172 metres, taller than any other church in the world.”',
      wrong: 'The guide smiles: “Try again — start with ‘Excuse me…’”',
      phrase: 'Excuse me, when will the basilica be finished?',
    },
    guellBench: {
      text: 'The serpentine bench on the terrace of Park Güell: 110 metres of curves covered in trencadís — broken tiles glued into mosaics. From here you see the whole city down to the sea.',
      sit: '🪑 Sit down and look at the city',
      sat: 'Blue, yellow, white… every tile is a different shape. Down below, the towers of the Sagrada Família poke up above the roofs.',
      phrase: 'What a beautiful view!',
      q: 'What is “trencadís”?',
      a: ['A mosaic made of broken tiles', 'A kind of Catalan bread', 'A long wooden bench'],
      right: 'Right! Gaudí used tiles that the factories threw away. Nothing wasted.',
      wrong: 'Look at the bench: it is made of thousands of little broken pieces…',
    },
    dragon: {
      text: 'El drac: the mosaic salamander on the stairway, the most photographed animal in Barcelona. Water trickles from its mouth into the basin.',
      photo: '📸 Take the photo',
      done: 'Click! Everyone else is waiting their turn… Taiwan has a lot of dragons too — but this one is covered in tiles.',
      q: 'The dragon is really a…',
      a: ['Salamander', 'Crocodile', 'Cat'],
      right: 'A salamander! Gaudí loved animals and put them all over the park.',
      wrong: 'Look at its short legs and long tail…',
    },
    canaletes: {
      text: 'The Canaletes fountain at the top of La Rambla. They say: whoever drinks from it will come back to Barcelona. Fans of FC Barcelona celebrate their wins right here.',
      drink: '🚰 Drink the water',
      drank: 'Cold and fresh! Now you have to come back some day.',
      phrase: 'I will come back to Barcelona!',
    },
    statue: {
      text: 'A human statue: a man painted bronze, standing completely still as a Columbus. Drop a coin and… something happens.',
      tip: '🪙 Drop a coin (2 coins)',
      moved: 'The statue blinks, bows, and points the way to the sea with a creak. Then freezes again!',
      poor: 'No coins for the statue today.',
    },
    columbus: {
      text: 'The Columbus column, 60 metres tall, at the foot of La Rambla. Built for the 1888 World Fair. Columbus points out to sea… towards the east, which is not even where America is!',
      look: '🔭 Look where he points',
      q: 'Which sea is Columbus pointing at?',
      a: ['The Mediterranean', 'The Pacific', 'The Atlantic'],
      right: 'Yes! Barcelona is on the Mediterranean Sea. Taiwan is on the Pacific.',
      wrong: 'Hmm… Barcelona’s sea is warm, blue and has no big waves. Which one is it?',
    },
    cathedral: {
      text: 'The Cathedral of Santa Eulàlia, built between 1298 and 1420. In its cloister live thirteen white geese — one for every year of the saint’s short life.',
      geese: '🪿 Count the geese',
      q: 'How many geese live in the cloister?',
      a: ['Thirteen', 'Three', 'Thirty'],
      right: 'Thirteen, like Eulàlia’s age. And on Sundays people dance the sardana right outside.',
      wrong: 'As many as the years the saint lived: she was very young…',
    },
    castellers: {
      text: 'Castellers build human towers up to ten people high. The base is the “pinya”; the small kid who climbs to the top is the “enxaneta” and raises one hand when the tower is complete.',
      cheer: '📣 Cheer the enxaneta',
      cheered: 'The crowd goes quiet… the enxaneta raises her hand… “Castell carregat!” Everyone claps. Then they climb down just as carefully.',
      q: 'The motto of the castellers is “Força, equilibri, valor i seny”. What does “seny” mean?',
      a: ['Good sense', 'Noise', 'Speed'],
      right: 'Good sense: being careful and calm. You need all four to build a tower.',
      wrong: 'Think: what do you need so nobody falls? Strength, balance, courage… and…',
    },
    petanca: {
      text: 'Petanca: throw the steel boule closest to the little wooden “cochonnet”. Grandpas play here every afternoon.',
      throw: '🎯 Throw a boule',
      result: (cm: number) => `Your boule stops ${cm} cm from the cochonnet.`,
      great: 'Closest of all! The grandpas nod: “molt bé”.',
      again: '🎯 Throw again',
    },

    // --- Food ------------------------------------------------------------------------------------
    flowers: {
      vendor: 'Hello! Roses, tulips, sunflowers — what would you like?',
      ok: 'One sunflower, please.',
      bad: ['Give flower.', 'Sunflower me want.'],
      done: '🌻 One sunflower, wrapped in paper. It makes everyone smile on the way down La Rambla.',
      price: 6,
    },
    boqueria: {
      text: 'La Boqueria: the market of Sant Josep, since 1840. Paper cups of fruit juice in every colour, jamón, fruit, fish… Markets here open in the morning and close by the afternoon.',
      closed: 'The Boqueria is closed now. Markets in Barcelona open early and close in the afternoon — unlike Taiwan’s night markets!',
      juice: { vendor: 'Juices! Mango, strawberry, coconut, dragon fruit… which one?', ok: 'A mango juice, please.', bad: ['Mango juice now.', 'Juice of mango give.'], done: '🥭 Icy mango juice in a paper cup. One sip and the heat of La Rambla is gone.', price: 4 },
      ham: { vendor: 'Jamón ibérico, cut thin — a paper cone for you?', ok: 'A cone of jamón, please.', bad: ['Jamón cone me.', 'Want jamón, cone.'], done: '🍖 A paper cone of jamón, salty and soft. In Spain it is cut by hand, slice by slice.', price: 8 },
      fruit: { vendor: 'Fresh fruit cups — melon, kiwi, strawberries!', ok: 'A fruit cup, please.', bad: ['Fruit cup, now!', 'Cup fruit I take.'], done: '🍓 A cup of cut fruit with a little wooden fork. Sweet!', price: 5 },
    },
    xurros: {
      vendor: 'Churros with thick hot chocolate — breakfast or afternoon snack?',
      ok: 'Churros with chocolate, please.',
      bad: ['Churros chocolate give.', 'Me churros now.'],
      done: '🍫 Crunchy churros dipped in chocolate so thick the spoon stands up. Here people eat them for breakfast — or after a party at dawn!',
      price: 6,
    },
    chiringuito: {
      text: 'A xiringuito on the sand: paella, crema catalana, cold horchata. In Spain lunch is at two or three and dinner at nine — much later than in Taiwan!',
      noPaella: 'The waiter: “Paella? Not yet — lunch here starts at two! Horchata or a crema catalana while you wait?”',
      paella: { vendor: 'Paella for one? Rice, prawns, mussels, a slice of lemon.', ok: 'A paella, please.', bad: ['Paella me.', 'Give paella now.'], done: '🥘 Yellow rice with prawns and lemon, with the sea in front of you. The best bit is the crust at the bottom: the “socarrat”.', price: 18 },
      crema: { vendor: 'Crema catalana: custard with burnt sugar on top. Crack it with the spoon!', ok: 'A crema catalana, please.', bad: ['Crema give.', 'Catalana crema now.'], done: '🍮 Crack! The sugar breaks like glass, and underneath it is cold and creamy.', price: 6 },
      horchata: { vendor: 'Cold horchata — made from tiger nuts, very sweet.', ok: 'A horchata, please.', bad: ['Horchata me want.', 'Give horchata.'], done: '🥛 Sweet, cold and milky. Nothing to do with the soy milk at home!', price: 4 },
    },

    // --- NPC talk ---------------------------------------------------------------------------------
    talk: {
      bc_florist: [
        'Flowers on La Rambla since 1850! On Sant Jordi, the 23rd of April, everyone gives a rose and a book. “A rose for you!”',
        'The tulips come from Holland, the roses from here. “Which one do you like?”',
      ],
      bc_guide: [
        'Gaudí said: “Nature is my teacher.” Look at the columns inside: they branch like trees. “Welcome to the Sagrada Família!”',
        'Gaudí worked on this for 43 years and is buried in the crypt. People pay for the works with their tickets — no money from the government.',
      ],
      bc_waiter: [
        'Lunch? Not before two o’clock, that is how we do it here. And dinner at nine or ten! “Are you hungry?”',
        'Here we pay in euros, not in Taiwan dollars. One euro is about 35 Taiwan dollars, so a paella is… a lot of dollars! “The bill, please.”',
      ],
      bc_avi: [
        'Every morning: a baguette from the forn and a coffee. Pa amb tomàquet — bread rubbed with tomato, oil and salt. Try it! “Good morning!”',
        'When I was a kid we paid in pesetas. Then euros came in 2002. Now I count everything twice!',
      ],
      bc_busker: [
        '♪ Rumba catalana, the music of the Gòtic streets. A coin in the hat and I play your song. “Thank you, my friend!”',
        'I learned on this guitar from my grandfather. The plaza has the best echo in Barcelona.',
      ],
      bc_sardanista: [
        'The sardana: we hold hands in a circle and count the steps — short ones and long ones. Anyone can join, even you! “Come and dance!”',
        'The band is called a cobla. The high flute is the flabiol: the player plays it with one hand and a drum with the other.',
      ],
      bc_petancaMan: [
        'Every afternoon, same bench, same friends, same boules. The secret is the wrist. “Your turn!”',
        'Petanca comes from the south of France, but we have been playing here for a hundred years.',
      ],
      bc_juicer: [
        'Two euros a cup, three for the big one. We open at eight and close at three — then we go home for lunch! “Fresh juice!”',
        'Dragon fruit, mango, pineapple, coconut… the colours sell themselves.',
      ],
      bc_kid: [
        'I skate the whole promenade, from the W hotel to the port, in twelve minutes! “Catch me if you can!”',
        'In summer the water is warm until October. Today? A bit cold, but my dad says I am made of rubber.',
      ],
      bc_staff: [
        'Welcome to Barcelona! Aerobús to the centre every five minutes, or the L9 metro. “Can I help you?”',
        'Flying home? International flights leave from T1 — go through security early, Barcelona airport is big.',
      ],
      bc_casteller: [
        'I am the cap de colla — I decide which tower we build. Today: a four of eight. Eight levels, four people each. “Up we go!”',
        'The strongest people are at the bottom, in the pinya, and the lightest kid at the top. Everyone has a job.',
      ],
      bc_painter: [
        'A portrait in ten minutes, charcoal or pastel. I have drawn tourists from forty countries… and one from Taiwan: you? “Sit down, please!”',
        'The hard part is the smile. Everyone stops smiling after three minutes.',
      ],
    },
    tourist: {
      intro: '你好！請問，聖家堂怎麼走？我迷路了…\n\n(Hello! Excuse me, how do I get to the Sagrada Família? I’m lost…)',
      help: '🗣️ Help in Chinese',
      q: '怎麼回答他？ (How do you answer?)',
      a: ['坐地鐵 L5 藍線，到 Sagrada Família 站下車。 (Take the blue L5 metro to Sagrada Família.)', '一直走到海邊。 (Keep walking to the sea.)', '坐飛機回台灣。 (Take a plane back to Taiwan.)'],
      right: '「太好了，謝謝你！」He bows twice, very relieved — and gives you a pineapple cake from his bag. 🍍',
      wrong: '「海邊？」He looks at his map, confused. The basilica is to the north, by the metro…',
    },
    hours: 'In Barcelona, lunch is at 2 pm and dinner at 9 pm. Shops close for a long lunch and the market shuts in the afternoon.',
  },
};
